/**
 * Cloudflare Turnstile for the email sign-in form (design §4.4: the start
 * route refuses a request without a fresh token, so a script cannot mail
 * arbitrary addresses through us).
 *
 * The script is loaded only when the email form is on screen, never on a page
 * whose URL carries a credential (the chooser's URL has none by then: OAuth
 * parameters are stripped on arrival, and an email link never reaches it).
 * A token is single use — Cloudflare's siteverify refuses a second redemption
 * — so the widget is reset after every send.
 */
import { onBeforeUnmount, ref, type Ref } from 'vue'

interface TurnstileApi {
  render(element: HTMLElement, options: Record<string, unknown>): string
  reset(widgetId: string): void
  remove(widgetId: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let scriptLoad: Promise<TurnstileApi> | null = null

function loadScript(): Promise<TurnstileApi> {
  scriptLoad ??= new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile)
    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')))
    script.onerror = () => {
      // Let a later mount try again (a network blip, an extension blocking it).
      scriptLoad = null
      reject(new Error('turnstile'))
    }
    document.head.appendChild(script)
  })
  return scriptLoad
}

export function useTurnstile(container: Ref<HTMLElement | null>, siteKey: () => string | null, language: () => string) {
  /** The current token; null until the visitor passes, and after each use. */
  const token = ref<string | null>(null)
  /** The widget could not load: the form explains instead of sending nothing. */
  const failed = ref(false)
  let widgetId: string | null = null
  /** The element the widget lives in: the form is re-rendered between steps, taking it along. */
  let mountedOn: HTMLElement | null = null

  async function mount(): Promise<void> {
    const key = siteKey()
    if (!key || !container.value) return
    if (widgetId && mountedOn === container.value) return
    try {
      const api = await loadScript()
      const element = container.value
      if (!element || (widgetId && mountedOn === element)) return
      if (widgetId) api.remove(widgetId)
      token.value = null
      mountedOn = element
      widgetId = api.render(element, {
        sitekey: key,
        // The API checks it (handlers/emailAuth.ts EMAIL_START_TURNSTILE_ACTION): a token
        // minted by a widget for anything else is refused.
        action: 'email_start',
        language: language(),
        // 'flexible' fills the form's width on a phone instead of a fixed 300px.
        size: 'flexible',
        // Shown only when Cloudflare needs the visitor to click; most never see it.
        appearance: 'interaction-only',
        callback: (value: string) => (token.value = value),
        'expired-callback': () => (token.value = null),
        'error-callback': () => {
          token.value = null
        },
      })
      failed.value = false
    } catch {
      failed.value = true
    }
  }

  /** After a send: the token is spent, ask for a new one. */
  function reset(): void {
    token.value = null
    if (widgetId && window.turnstile) window.turnstile.reset(widgetId)
  }

  onBeforeUnmount(() => {
    if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    widgetId = null
    mountedOn = null
  })

  return { token, failed, mount, reset }
}
